import { useEffect, useMemo, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import frameSvg from 'assets/icons/frame.svg';
import { orderByField } from 'components/common/Deal/ListOfFiles/useListOfFiles';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { SortDir, SortSettings } from 'components/common/types/SortDir.types';
import CollapsibleList from 'lib/CollapsibleList';
import CollapsibleListStatsHeader from 'lib/CollapsibleList/CollapsibleListStatsHeader';
import SelectOption from 'lib/SelectOption';
import { getIsReseller } from 'store/publisher/selectors';

export type SelectedOptionsCollapsibleProps = {
  onToggleCategory: React.Dispatch<React.SetStateAction<string>>;
  openCategories: string[];
  setSelectedRouteFrameCodes: React.Dispatch<React.SetStateAction<RouteFrameCode[]>>;
  onOptionIncludeExcludeChange: (routeFrameCode: RouteFrameCode) => void;
  isOptionIncluded: (code: string, mediaOwner?: string) => boolean | undefined;
  isOptionSelected: (code: string, mediaOwner?: string) => boolean;
  selectedRouteFrameCodes: RouteFrameCode[];
  label: string;
  isExcludeHidden?: boolean;
};

const SelectedOptionsCollapsible: React.FC<SelectedOptionsCollapsibleProps> = ({
  selectedRouteFrameCodes,
  setSelectedRouteFrameCodes,
  onOptionIncludeExcludeChange,
  isOptionIncluded,
  isOptionSelected,
  onToggleCategory,
  openCategories,
  label,
  isExcludeHidden = false,
}) => {
  const isReseller = useSelector(getIsReseller);

  const [sortDirection, setSortDirection] = useState(SortDir.ASC);
  const [selectedFrames, setSelectedFrames] = useState(selectedRouteFrameCodes);

  useEffect(() => {
    setSelectedFrames(selectedRouteFrameCodes);
  }, [selectedRouteFrameCodes]);

  const onCategoryDirectionSort = (direction: SortDir): void => {
    const routeFrameCodes = cloneDeep(selectedRouteFrameCodes);

    setSortDirection(SortSettings[sortDirection].nextDir);

    if (!routeFrameCodes) return;

    const sorted = orderByField(routeFrameCodes, 'name', direction);

    setSelectedFrames(sorted);
  };

  const includedFramesLength = (): number => selectedRouteFrameCodes?.filter(({ include }) => include).length;

  const excludedFramesLength = (): number => selectedRouteFrameCodes?.filter(({ include }) => include === false).length;

  const hasOneFrameOrMore = selectedFrames.length >= 1;

  const onEntireIncludeExclude = ({ include }: { include: boolean }): void => {
    if (!hasOneFrameOrMore) return;

    setSelectedRouteFrameCodes(selectedRouteFrameCodes.map((frame) => ({ ...frame, include })));
  };

  const getCategoryIncludeState = useMemo(
    (): boolean | undefined =>
      hasOneFrameOrMore ? selectedRouteFrameCodes?.every((frame) => frame.include) : undefined,
    [hasOneFrameOrMore, selectedRouteFrameCodes],
  );

  return (
    <CollapsibleList
      label={label}
      onSort={onCategoryDirectionSort}
      isSortVisible={hasOneFrameOrMore}
      sortDir={sortDirection}
      isOpen={openCategories.includes(label)}
      onToggle={() => onToggleCategory(label)}
      isClearAllVisible={hasOneFrameOrMore}
      onClearAll={() => setSelectedRouteFrameCodes([])}
      onGroupInclude={hasOneFrameOrMore ? () => onEntireIncludeExclude({ include: true }) : undefined}
      onGroupExclude={hasOneFrameOrMore ? () => onEntireIncludeExclude({ include: false }) : undefined}
      hideGroupExclude={isExcludeHidden}
      groupIncludeState={getCategoryIncludeState}
      isGroupDeselectAllowed={false}
      key="format-category-selected-frame-list"
      headerComponent={
        <CollapsibleListStatsHeader
          available={selectedFrames.length}
          included={includedFramesLength()}
          excluded={excludedFramesLength()}
          availableLengthSuffix={<SVG src={frameSvg} />}
        />
      }
    >
      {selectedFrames.map(({ code, name, mediaOwner }: RouteFrameCode) => (
        <div
          className="border-b border-neutral-950-opacity-10 last:border-b-0"
          key={`selected-route-frame-codes-${code}-${mediaOwner}`}
        >
          <SelectOption
            value={{
              code,
              name,
              mediaOwner,
              include: isOptionIncluded(code, mediaOwner),
            }}
            isSelected={isOptionSelected(code, mediaOwner)}
            isIncludeExcludeAlwaysVisible
            hideExclude={isExcludeHidden}
            isDisabled={false}
            hasCheckbox={false}
            isMultiple
            onIncludeExcludeChange={onOptionIncludeExcludeChange}
          >
            <div>
              {isReseller && mediaOwner && <div>{mediaOwner}:</div>}
              {name}
            </div>
          </SelectOption>
        </div>
      ))}
    </CollapsibleList>
  );
};

export default SelectedOptionsCollapsible;
